import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { affiliatorGroupApi } from '@/services/subscriptionApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Users, Search, BarChart3, CheckSquare } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import type { AffiliatorGroup, AffiliatorStatus, CommissionModel, PaymentFrequency } from '@/types/subscription';

export const AffiliatorGroupListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPaymentFrequency, setFilterPaymentFrequency] = useState<string>('all');
  const [filterCommissionModel] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedAffiliators, setSelectedAffiliators] = useState<Set<string>>(new Set());
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'affiliatorGroup');
  const canEdit = hasPermission('edit', 'affiliatorGroup');
  const canApprove = hasPermission('approve', 'affiliatorGroup');

  // Build fetch function with filters and sorting
  const fetchAffiliatorsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      return affiliatorGroupApi.list({
        page: params.page,
        size: params.size,
        sort: `${sortBy},${sortDirection}`,
        status: filterStatus !== 'all' ? (filterStatus as AffiliatorStatus) : undefined,
        paymentFrequency: filterPaymentFrequency !== 'all' ? (filterPaymentFrequency as PaymentFrequency) : undefined,
        commissionModel: filterCommissionModel !== 'all' ? (filterCommissionModel as CommissionModel) : undefined,
        search: searchQuery.trim() || undefined,
      });
    };
  }, [searchQuery, filterStatus, filterPaymentFrequency, filterCommissionModel, sortBy, sortDirection]);

  // Use paginated list hook
  const {
    data: affiliators,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<AffiliatorGroup>({
    fetchFn: fetchAffiliatorsFn,
    entityName: 'affiliators',
    suppress404Errors: true,
  });

  // Status change mutation
  const statusChangeMutation = useMutation({
    mutationFn: async ({ id, action, reason }: { id: string; action: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'terminate'; reason?: string }) => {
      if (action === 'approve') {
        await affiliatorGroupApi.approve(id);
      } else if (action === 'reject') {
        await affiliatorGroupApi.reject(id, { reason: reason || '', notes: '' });
      } else if (action === 'suspend') {
        await affiliatorGroupApi.suspend(id, { reason: reason || '', notes: '' });
      } else if (action === 'reactivate') {
        await affiliatorGroupApi.reactivate(id);
      } else if (action === 'terminate') {
        await affiliatorGroupApi.terminate(id, { reason: reason || '', notes: '' });
      }
    },
    entityName: 'affiliator',
    operation: 'update',
    successMessage: (_data, variables) => `Affiliator ${variables.action}d successfully`,
    onSuccess: () => {
      refetch();
    },
  });

  // Bulk status change mutation
  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, action, reason }: { ids: string[]; action: 'approve' | 'reject' | 'suspend' | 'terminate'; reason?: string }) => {
      const promises = ids.map((id) => {
        if (action === 'approve') {
          return affiliatorGroupApi.approve(id);
        } else if (action === 'reject') {
          return affiliatorGroupApi.reject(id, { reason: reason || '', notes: '' });
        } else if (action === 'suspend') {
          return affiliatorGroupApi.suspend(id, { reason: reason || '', notes: '' });
        } else {
          return affiliatorGroupApi.terminate(id, { reason: reason || '', notes: '' });
        }
      });
      await Promise.all(promises);
    },
    entityName: 'affiliators',
    operation: 'update',
    successMessage: (_data, variables) => `${variables.ids.length} affiliator(s) ${variables.action}d`,
    onSuccess: () => {
      setSelectedAffiliators(new Set());
      refetch();
    },
  });

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleStatusChange = async (id: string, action: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'terminate', reason?: string) => {
    if ((action === 'reject' || action === 'suspend' || action === 'terminate') && !reason) {
      reason = window.prompt(`Enter reason for ${action}:`) || '';
      if (!reason) return;
    }
    await statusChangeMutation.mutateAsync({ id, action, reason });
  };

  const handleBulkStatusChange = async (action: 'approve' | 'reject' | 'suspend' | 'terminate') => {
    if (selectedAffiliators.size === 0) return;
    const reason = (action === 'reject' || action === 'suspend' || action === 'terminate')
      ? window.prompt(`Enter reason for bulk ${action}:`) || ''
      : '';
    if ((action === 'reject' || action === 'suspend' || action === 'terminate') && !reason) return;

    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${selectedAffiliators.size} affiliator(s)?`)) return;
    await bulkStatusMutation.mutateAsync({ ids: Array.from(selectedAffiliators), action, reason });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedAffiliators);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedAffiliators(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedAffiliators.size === affiliators.length) {
      setSelectedAffiliators(new Set());
    } else {
      setSelectedAffiliators(new Set(affiliators.map((a) => a.id)));
    }
  };

  const getStatusColor = (status: AffiliatorStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'SUSPENDED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'TERMINATED':
      case 'REJECTED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Affiliators</h1>
          <p className="text-muted-foreground mt-2">Manage affiliator groups</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button asChild>
              <Link to="/admin/affiliators/new">
                <Plus className="mr-2 h-4 w-4" />
                Onboard Affiliator
              </Link>
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/admin/affiliators/stats">
              <BarChart3 className="mr-2 h-4 w-4" />
              Statistics
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search affiliators..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
                <SelectItem value="SUSPENDED">Suspended</SelectItem>
                <SelectItem value="TERMINATED">Terminated</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPaymentFrequency} onValueChange={setFilterPaymentFrequency}>
              <SelectTrigger>
                <SelectValue placeholder="Payment Frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Frequencies</SelectItem>
                <SelectItem value="MONTHLY">Monthly</SelectItem>
                <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                <SelectItem value="YEARLY">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedAffiliators.size > 0 && canApprove && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {selectedAffiliators.size} affiliator(s) selected
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkStatusChange('approve')}
                  disabled={bulkStatusMutation.isLoading}
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkStatusChange('suspend')}
                  disabled={bulkStatusMutation.isLoading}
                >
                  Suspend
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedAffiliators(new Set())}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Affiliators Table */}
      <Card>
        <CardHeader>
          <CardTitle>Affiliators ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all affiliator groups</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading affiliators..." />
          ) : (error || affiliators.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load affiliators. Please try again or create a new one." : "Get started by onboarding your first affiliator."}
              icon={Users}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/affiliators/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {canApprove && (
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedAffiliators.size === affiliators.length && affiliators.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300"
                        />
                      </TableHead>
                    )}
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('groupCode')}
                    >
                      Group Code
                      {sortBy === 'groupCode' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('groupName')}
                    >
                      Group Name
                      {sortBy === 'groupName' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Company Name</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortBy === 'status' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Profit Share %</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('pendingPayouts')}
                    >
                      Pending Payouts
                      {sortBy === 'pendingPayouts' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('totalEarnings')}
                    >
                      Total Earnings
                      {sortBy === 'totalEarnings' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliators.map((affiliator) => (
                    <TableRow key={affiliator.id}>
                      {canApprove && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedAffiliators.has(affiliator.id)}
                            onChange={() => toggleSelect(affiliator.id)}
                            className="rounded border-gray-300"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">{affiliator.groupCode}</TableCell>
                      <TableCell className="font-medium">{affiliator.groupName}</TableCell>
                      <TableCell>{affiliator.companyName}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(affiliator.status)}`}>
                          {affiliator.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {affiliator.profitShareType === 'PERCENTAGE'
                          ? `${affiliator.profitSharePercentage}%`
                          : formatCurrency(affiliator.fixedCommissionAmount || 0, affiliator.preferredCurrency)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(affiliator.pendingPayouts, affiliator.preferredCurrency)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(affiliator.totalEarnings, affiliator.preferredCurrency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/affiliators/${affiliator.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/affiliators/${affiliator.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canApprove && affiliator.status === 'PENDING' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStatusChange(affiliator.id, 'approve')}
                              title="Approve"
                              disabled={statusChangeMutation.isLoading}
                            >
                              <CheckSquare className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {canApprove && affiliator.status === 'SUSPENDED' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStatusChange(affiliator.id, 'reactivate')}
                              title="Reactivate"
                              disabled={statusChangeMutation.isLoading}
                            >
                              <CheckSquare className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                    </div>
                    <Select value={pagination.size.toString()} onValueChange={(v) => setSize(parseInt(v))}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliatorGroupListPage;
